import React, { useState, useContext } from 'react';
import api from '../services/api';
import { ThemeContext } from '../context/ThemeContext';

const Contact = () => {
  const { darkMode } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError('');
    try {
      await api.post('/Contact', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        subject: formData.subject,
        message: formData.message,
      });
      setShowSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      if (err.response?.status === 429) {
        const mins = err.response.data?.minutesRemaining;
        setSubmitError(
          mins
            ? `Too many requests. Please try again in ${Math.ceil(mins)} minutes.`
            : 'Too many requests. Please try again later.'
        );
      } else {
        setSubmitError('Something went wrong. Please try again or email us directly.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-white dark:bg-gray-900`}>

      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 max-w-md w-full text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Message sent!</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Thanks for reaching out. We'll get back to you within 1–2 business days.
            </p>
            <button
              onClick={() => setShowSuccess(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-semibold"
            >
              Done
            </button>
          </div>
        </div>
      )}

      <div className="flex-grow">

        {/* Header */}
        <div className="text-center pt-16 pb-10 px-4">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white tracking-tight mb-4">
            Get in touch.
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            We reply within 1–2 business days. You can also email us at{' '}
            <a href="mailto:support@stonewallinvoicing.com" className="text-blue-600 dark:text-blue-400 hover:underline">support@stonewallinvoicing.com</a>{' '}
            or DM us on{' '}
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">Instagram</a>.
          </p>
        </div>

        <div className="max-w-2xl mx-auto px-4 pb-20">

            {/* Contact Form */}
            <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-8 md:p-12">
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange}
                      className="block w-full p-3 border-0 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-600 focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="Your name" required />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                      className="block w-full p-3 border-0 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-600 focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="your@email.com" required />
                  </div>
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
                    className="block w-full p-3 border-0 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-600 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="(555) 123-4567" />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                  <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange}
                    className="block w-full p-3 border-0 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-600 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="How can we help?" required />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                  <textarea id="message" name="message" rows={5} value={formData.message} onChange={handleChange}
                    className="block w-full p-3 border-0 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-600 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                    placeholder="Your message here..." required />
                </div>
                {submitError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                )}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 rounded-lg text-sm font-semibold transition-colors ${
                    isLoading
                      ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                  }`}
                >
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

        </div>

      </div>
    </div>
  );
};

export default Contact;
